import axios from "axios";

// Prefer SAME-ORIGIN in the browser whenever the current hostname doesn't match the build-time
// REACT_APP_BACKEND_URL. This eliminates iOS Safari / Android Chrome cross-origin
// credential-blocking on custom-domain production deploys (e.g. vjonesvault.com calling the
// emergent.host backend was failing silently on mobile).
const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const resolveBackendUrl = () => {
  if (typeof window === "undefined") return BUILD_BACKEND_URL;
  try {
    const here = window.location.hostname;
    if (!BUILD_BACKEND_URL) return ""; // same-origin
    const buildHost = new URL(BUILD_BACKEND_URL).hostname;
    // If the page is served from a different host than the baked backend URL,
    // assume the same domain ALSO proxies /api (true on Emergent custom-domain
    // deploys + on preview) and use same-origin. Falls back to the baked URL otherwise.
    if (here && buildHost && here !== buildHost) return "";
    return BUILD_BACKEND_URL;
  } catch {
    return BUILD_BACKEND_URL;
  }
};

const BACKEND_URL = resolveBackendUrl();
export const ORIGIN = BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Normalize FastAPI / Pydantic v2 422 responses so every caller that reads
// `err.response.data.detail` for a toast always gets a STRING. FastAPI
// returns `detail: [{type, loc, msg, input, url}, ...]` on validation
// errors, and React crashes if any consumer renders that array as a child
// (which is exactly how `toast.error(err.response.data.detail)` would
// blow up). Collapse it to the human `msg` field — joined when there are
// multiple — and leave string/single-object details untouched.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const d = err?.response?.data;
      if (d && Array.isArray(d.detail)) {
        d.detail = d.detail
          .map((e) => (e && typeof e === "object" ? (e.msg || JSON.stringify(e)) : String(e)))
          .join(" · ");
      } else if (d && typeof d.detail === "object" && d.detail !== null) {
        d.detail = d.detail.msg || JSON.stringify(d.detail);
      }
    } catch { /* never let the interceptor swallow the original error */ }
    return Promise.reject(err);
  },
);

export const fetchCategories = () => api.get("/categories").then((r) => r.data);
export const fetchDossiers = (params = {}) => api.get("/dossiers", { params }).then((r) => r.data);
export const fetchFeatured = () => api.get("/dossiers/featured").then((r) => r.data);
export const fetchDossier = (slug) =>
  api.get(`/dossiers/${slug}`, { timeout: 10000 }).then((r) => r.data);

/**
 * Unified search across ALL dossier-like content. ANY caller that wants to
 * let users find "a dossier" should use this — never raw /dossiers — so that
 * AI-compiled deep dives and Connect-the-Dots connections are always
 * included alongside the curated dossiers.
 *
 * Returns a normalized array of items, each:
 *   {
 *     type: 'dossier' | 'ai_dossier' | 'connection',
 *     title: string,
 *     subtitle: string,   // category + credibility/source line
 *     slug: string,       // routing key (slug for dossiers, id for the rest)
 *     url: string,        // ready-to-use route
 *     category: string?,
 *     credibility: string?,
 *   }
 *
 * Server-side `/dossiers` already supports `?q=`. AI deep dives and
 * connections don't have a `q` param, so we filter client-side. They're
 * paginated to small batches anyway (published only).
 */
export const searchAllDossiers = async (rawQ, limit = 12) => {
  const q = (rawQ || "").trim().toLowerCase();
  if (!q) return [];

  const [dossiers, dives, conns] = await Promise.all([
    api.get("/dossiers", { params: { q, limit } }).then((r) => r.data || []).catch(() => []),
    api.get("/deep-dive/published").then((r) => r.data || []).catch(() => []),
    api.get("/connect-dots/published", { timeout: 10000 }).then((r) => r.data || []).catch(() => []),
  ]);

  const matchesQ = (s) => (s || "").toLowerCase().includes(q);

  const out = [];
  for (const d of dossiers.slice(0, limit)) {
    out.push({
      type: "dossier",
      title: d.title,
      subtitle: [d.category, (d.credibility || "").toUpperCase()].filter(Boolean).join(" · "),
      slug: d.slug,
      url: `/dossier/${d.slug}`,
      category: d.category,
      credibility: d.credibility,
    });
  }
  // AI dossiers — match against topic + report body fragment if present
  for (const d of dives) {
    if (!matchesQ(d.topic) && !matchesQ(d.report)) continue;
    out.push({
      type: "ai_dossier",
      title: d.topic || "AI Deep Dive",
      subtitle: `AI · ${(d.inferred_credibility || "AI_COMPILED").toUpperCase()}`,
      slug: d.id,
      url: `/ai-dossier/${d.id}`,
      category: null,
      credibility: d.inferred_credibility || "ai_compiled",
    });
    if (out.length >= limit * 2) break;
  }
  // Connections — match title + any item in the items array
  for (const c of conns) {
    const haystack = `${c.title || ""} ${(c.items || []).join(" ")}`;
    if (!matchesQ(haystack)) continue;
    out.push({
      type: "connection",
      title: c.title || "Connection",
      subtitle: `CONNECTION · ${(c.items || []).length} SUBJECTS`,
      slug: c.id,
      url: `/connection/${c.id}`,
      category: null,
      credibility: null,
    });
    if (out.length >= limit * 3) break;
  }
  return out.slice(0, limit * 3);
};
// --- Polling helpers — keep the public API signatures the same (single Promise that resolves with
// the COMPLETE artifact) but internally drive the new BackgroundTasks pattern. The kickoff POST
// returns in <1s with a 'generating' placeholder; we then poll GET until status flips to 'done'/'error'.
//
// Polling cadence: ADAPTIVE. Start at 800ms for the first 5 attempts (so a
// fast Deep Dive that finishes in 2-3s feels instant), then back off to a
// 4s steady-state interval. Total time-budget unchanged at ~5 minutes.
const POLL_FAST_INTERVAL_MS = 800;
const POLL_SLOW_INTERVAL_MS = 4000;
const POLL_FAST_ATTEMPTS = 5;          // first 5 attempts at fast cadence (~4s)
const POLL_MAX_ATTEMPTS = 80;          // 5 fast + 75 slow = ~5 min total

// How long to wait before the i-th poll attempt (0-indexed). Used so callers
// don't have to think about cadence — just `await pollDelay(i)`.
function _pollDelay(i) {
  const ms = i < POLL_FAST_ATTEMPTS ? POLL_FAST_INTERVAL_MS : POLL_SLOW_INTERVAL_MS;
  return new Promise((r) => setTimeout(r, ms));
}

async function _waitForDive(id, onProgress) {
  let lastEventCount = 0;
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await _pollDelay(i);
    try {
      const fresh = await api.get(`/deep-dive/${id}`, { timeout: 10000 }).then((r) => r.data);
      if (typeof onProgress === "function") {
        const evts = fresh.progress_events || [];
        if (evts.length !== lastEventCount) {
          lastEventCount = evts.length;
          try { onProgress(evts); } catch { /* never crash polling on UI errors */ }
        }
      }
      if (fresh.generation_status === "done") return fresh;
      if (fresh.generation_status === "error") {
        const err = new Error(fresh.generation_error || "Research engine failure");
        err.response = { data: { detail: fresh.generation_error || "Research engine failure" } };
        throw err;
      }
    } catch (e) {
      if (e?.response?.data?.detail) throw e; // permanent error — rethrow immediately
      // else: transient network blip, keep polling
    }
  }
  throw new Error("Research engine timed out after 5 minutes — try again.");
}

async function _waitForConnection(id, onProgress) {
  let lastEventCount = 0;
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await _pollDelay(i);
    try {
      const fresh = await api.get(`/connect-dots/${id}`, { timeout: 10000 }).then((r) => r.data);
      if (typeof onProgress === "function") {
        const evts = fresh.progress_events || [];
        if (evts.length !== lastEventCount) {
          lastEventCount = evts.length;
          try { onProgress(evts); } catch { /* */ }
        }
      }
      if (fresh.generation_status === "done") return fresh;
      if (fresh.generation_status === "error") {
        const err = new Error(fresh.generation_error || "Connections analyst failure");
        err.response = { data: { detail: fresh.generation_error || "Connections analyst failure" } };
        throw err;
      }
    } catch (e) {
      if (e?.response?.data?.detail) throw e;
    }
  }
  throw new Error("Connections analyst timed out after 5 minutes — try again.");
}

async function _waitForFollowUp(diveId) {
  // The follow-up patches the LAST assistant message in `messages[]` — status flips on that placeholder.
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await _pollDelay(i);
    try {
      const fresh = await api.get(`/deep-dive/${diveId}`, { timeout: 10000 }).then((r) => r.data);
      const msgs = fresh.messages || [];
      const last = msgs[msgs.length - 1];
      if (last && last.role === "assistant") {
        if (!last.status || last.status === "done") return fresh;
        if (last.status === "error") {
          const err = new Error(last.content || "Follow-up failed");
          err.response = { data: { detail: last.content || "Follow-up failed" } };
          throw err;
        }
      }
    } catch (e) {
      if (e?.response?.data?.detail) throw e;
    }
  }
  throw new Error("Follow-up timed out after 5 minutes — try again.");
}

export const runDeepDive = async (topic, angle, skeptical = false, onProgress, opts = {}) => {
  // Kickoff is fast (<5s). If the backend already returns a finished doc (legacy path), short-circuit.
  const initial = await api.post(
    "/deep-dive",
    { topic, angle, skeptical, mode: opts.mode || "standard" },
    { timeout: 30000 },
  ).then((r) => r.data);
  if (typeof onProgress === "function" && initial.progress_events?.length) {
    try { onProgress(initial.progress_events); } catch { /* */ }
  }
  if (initial.generation_status === "done") return initial;
  if (initial.generation_status === "error") {
    throw new Error(initial.generation_error || "Research engine failure");
  }
  return _waitForDive(initial.id, onProgress);
};
export const fetchRecentDeepDives = () => api.get("/deep-dive/recent").then((r) => r.data);
export const fetchPublishedDeepDives = () => api.get("/deep-dive/published").then((r) => r.data);
export const fetchDeepDive = (id) => api.get(`/deep-dive/${id}`).then((r) => r.data);
export const publishDeepDive = (id, opts = {}) =>
  api.post(`/deep-dive/${id}/publish`, { cosigns_allowed: opts.cosignsAllowed !== false }).then((r) => r.data);
export const sendFollowUp = async (id, message) => {
  const kickoff = await api.post(
    `/deep-dive/${id}/message`,
    { message },
    { timeout: 30000 },
  ).then((r) => r.data);
  const msgs = kickoff.messages || [];
  const last = msgs[msgs.length - 1];
  // Legacy backend (sync) returns the finished assistant message with no status
  if (!last || last.role !== "assistant" || !last.status || last.status === "done") return kickoff;
  if (last.status === "error") {
    throw new Error(last.content || "Follow-up failed");
  }
  return _waitForFollowUp(id);
};
export const spawnSkepticalPair = async (id, onProgress) => {
  const initial = await api.post(`/deep-dive/${id}/skeptical`, {}, { timeout: 30000 }).then((r) => r.data);
  if (typeof onProgress === "function" && initial.progress_events?.length) {
    try { onProgress(initial.progress_events); } catch { /* */ }
  }
  if (initial.generation_status === "done") return initial;
  if (initial.generation_status === "error") {
    throw new Error(initial.generation_error || "Skeptical pair failed");
  }
  return _waitForDive(initial.id, onProgress);
};
export const fetchCommentCounts = (type) =>
  api.get(`/comments-counts/${type}`, { timeout: 10000 }).then((r) => r.data);
export const fetchSuggestions = (limit = 8) =>
  api.get(`/suggestions?limit=${limit}`, { timeout: 10000 }).then((r) => r.data);

// Connect the Dots
export const runConnectDots = async (items, context, onProgress, opts = {}) => {
  const initial = await api.post(
    "/connect-dots",
    {
      items,
      context,
      factions: opts.factions || null,
      mode: opts.mode || "default",
    },
    { timeout: 30000 },
  ).then((r) => r.data);
  if (typeof onProgress === "function" && initial.progress_events?.length) {
    try { onProgress(initial.progress_events); } catch { /* */ }
  }
  if (initial.generation_status === "done") return initial;
  if (initial.generation_status === "error") {
    throw new Error(initial.generation_error || "Connections analyst failure");
  }
  return _waitForConnection(initial.id, onProgress);
};
export const publishConnection = (id, opts = {}) =>
  api.post(`/connect-dots/${id}/publish`, { cosigns_allowed: opts.cosignsAllowed !== false }).then((r) => r.data);
export const fetchConnection = (id) =>
  api.get(`/connect-dots/${id}`, { timeout: 10000 }).then((r) => r.data);
export const fetchPublishedConnections = () =>
  api.get(`/connect-dots/published`, { timeout: 10000 }).then((r) => r.data);

// Connect-the-Dots continuous chat — mirrors the deep-dive follow-up pattern.
// Polls GET /connect-dots/{id} until the trailing assistant message flips to
// status: "done" (or "error"), then resolves with the freshest connection doc.
async function _waitForConnFollowUp(connId) {
  for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const fresh = await api.get(`/connect-dots/${connId}`, { timeout: 10000 }).then((r) => r.data);
      const msgs = fresh.messages || [];
      const last = msgs[msgs.length - 1];
      if (last && last.role === "assistant") {
        if (!last.status || last.status === "done") return fresh;
        if (last.status === "error") {
          const err = new Error(last.content || "Connection follow-up failed");
          err.response = { data: { detail: last.content || "Connection follow-up failed" } };
          throw err;
        }
      }
    } catch (e) {
      if (e?.response?.data?.detail) throw e;
    }
  }
  throw new Error("Connection follow-up timed out after 5 minutes — try again.");
}

export const sendConnectionFollowUp = async (id, message) => {
  const kickoff = await api.post(
    `/connect-dots/${id}/message`,
    { message },
    { timeout: 30000 },
  ).then((r) => r.data);
  const msgs = kickoff.messages || [];
  const last = msgs[msgs.length - 1];
  if (!last || last.role !== "assistant" || !last.status || last.status === "done") return kickoff;
  if (last.status === "error") {
    throw new Error(last.content || "Follow-up failed");
  }
  return _waitForConnFollowUp(id);
};

// Stateless chat helpers for Source Triangulator + Question Mode. Frontend
// keeps the conversation in component state and ships the running history
// every turn — backend persists nothing.
export const triangulateChat = (context, messages) =>
  api.post("/ai/triangulate/chat", { context, messages }, { timeout: 120000 }).then((r) => r.data);

export const questionModeChat = (context, messages) =>
  api.post("/ai/question-mode/chat", { context, messages }, { timeout: 120000 }).then((r) => r.data);

// Related dossiers
export const fetchRelatedDossiers = (slug, limit = 4) =>
  api.get(`/dossiers/${slug}/related`, { params: { limit }, timeout: 10000 }).then((r) => r.data);

// Clusters — themed groupings of all dossiers
export const fetchClusters = () =>
  api.get(`/dossier-clusters`, { timeout: 10000 }).then((r) => r.data);

// Random dossier (discovery)
export const fetchRandomDossier = () =>
  api.get(`/random-dossier`, { timeout: 8000 }).then((r) => r.data);

// Live public counts ({dossiers, deep_dives, connections, total})
export const fetchPublicCounts = () =>
  api.get(`/public-counts`, { timeout: 6000 }).then((r) => r.data);

export const fetchDailyFind = () =>
  api.get(`/daily-find`, { timeout: 6000 }).then((r) => r.data);

export const fetchWire = (limit = 10) =>
  api.get(`/wire/recent`, { params: { limit }, timeout: 6000 }).then((r) => r.data);

export const fetchTrendingNow = (hours = 24) =>
  api.get(`/trending-searches`, { params: { hours }, timeout: 8000 }).then((r) => r.data);

// Trending — top viewed dossiers / ai / connections in the last N days
export const fetchTrending = (limit = 5, windowDays = 7) =>
  api.get(`/trending`, { params: { limit, window_days: windowDays }, timeout: 8000 }).then((r) => r.data);

// Reactions — like / dislike
export const fetchReactionCounts = (targetType) =>
  api.get(`/reactions/counts/${targetType}`, { timeout: 10000 }).then((r) => r.data);
export const fetchMyReactions = (targetType, sessionId) =>
  api.get(`/reactions/mine/${targetType}`, { params: { session_id: sessionId }, timeout: 10000 }).then((r) => r.data);
export const fetchReaction = (targetType, targetId, sessionId) =>
  api.get(`/reactions/${targetType}/${targetId}`, { params: { session_id: sessionId }, timeout: 6000 }).then((r) => r.data);
export const toggleReaction = (targetType, targetId, sessionId, reaction) =>
  api.post(`/reactions/toggle`, { target_type: targetType, target_id: targetId, session_id: sessionId, reaction }).then((r) => r.data);

// Community facts
export const fetchCommunityFacts = (targetType, targetId) =>
  api.get(`/community-facts/${targetType}/${targetId}`).then((r) => r.data);
export const submitCommunityFact = (payload) =>
  api.post(`/community-facts`, payload).then((r) => r.data);


// Belief votes — "How much of this story do you believe?"
export const fetchBelief = (targetType, targetId, voterId) =>
  api.get(`/belief/${targetType}/${targetId}`, { params: { voter_id: voterId } }).then((r) => r.data);
export const castBelief = (targetType, targetId, voterId, score) =>
  api.post(`/belief/vote`, { target_type: targetType, target_id: targetId, voter_id: voterId, score }).then((r) => r.data);
export const clearBelief = (targetType, targetId, voterId) =>
  api.delete(`/belief/${targetType}/${targetId}`, { params: { voter_id: voterId } }).then((r) => r.data);

// Fact debate — support/dispute votes + threaded replies
export const fetchFactDebate = (factId, voterId) =>
  api.get(`/facts/${factId}/debate`, { params: { voter_id: voterId } }).then((r) => r.data);
export const castFactStance = (factId, voterId, stance) =>
  api.post(`/facts/stance`, { fact_id: factId, voter_id: voterId, stance }).then((r) => r.data);
export const postFactReply = (factId, voterId, stance, text, author, sourceUrl) =>
  api.post(`/facts/reply`, {
    fact_id: factId, voter_id: voterId, stance, text,
    author: author || null, source_url: sourceUrl || null,
  }).then((r) => r.data);


// My Ties boards (for profile pin selector)
export const fetchMyWarrooms = () =>
  api.get(`/warroom/list/mine`, { timeout: 10000 }).then((r) => r.data?.items || []);
